import { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import useAuth from "./useAuth";
import { useSnackbar } from "notistack";
import useMutationAuth from "./useMutationAuth";
import { useTranslation } from "react-i18next";
import { LOGOUT_USER } from "../graphql/user";
import { useDispatch } from "react-redux";
import { store } from "../app/store";
import { saveToken } from "../app/reducers/tokenSlice";

function useQueryAuth(
	QUERY,
	objAccess = "",
	params = {},
	statusFunctions = {},
	isVersion = false,
	notifyOnNetworkStatusChange = false
) {
	const { logout } = useAuth();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const [refetchedData] = useState(null);
	const [tokenChange, setTokenChange] = useState(true);

	const { mfunction: logsOutUser } = useMutationAuth(
		LOGOUT_USER,
		"LogoutUser"
	);

	useEffect(() => {
		if (params && params.skip === true && !params.isWait) {
			setTokenChange(false);
		}
	}, [params, params.skip, params.isWait]);

	const { loading, error, data, refetch } = useQuery(QUERY, {
		fetchPolicy: "no-cache",
		nextFetchPolicy: "no-cache",
		notifyOnNetworkStatusChange,
		...params,
		context: {
			headers: {
				authorization: store?.getState()?.token?.token
					? "Bearer " + store?.getState()?.token?.token
					: "",
			},
		},
		onCompleted: (data) => {
			if (data[objAccess].errorMessage === "IXIA_ACTIV_UNAUTHORIZED") {
				// console.error(
				//   `%c [useQueryAuth] Unauthorized access detected at ${new Date().toLocaleTimeString()}`,
				//   "color: red; font-weight: bold;"
				// );
				setTokenChange(false);
				sessionStorage.removeItem("unsavedChanges");
				logsOutUser(
					{
						variables: {
							model: {
								jwtToken: store?.getState()?.token?.token,
							},
						},
					},
					() => {
						logout();
					}
				);
			} else if (data[objAccess].errorMessage === "IXIA_ACTIV_NEW_TOKEN") {
				sessionStorage.removeItem("unsavedChanges");
				setTokenChange(true);
				const newToken = data[objAccess].errorVariables.trim();
				// console.log(
				//   `%c [useQueryAuth] Refresh token triggered at ${new Date().toLocaleTimeString()}`,
				//   "color: orange; font-weight: bold;"
				// );
				// console.log(
				//   `%c [useQueryAuth] Old token: ${store?.getState()?.token?.token}`,
				//   "color: purple; font-weight: bold;"
				// );
				// console.log(
				//   `%c [useQueryAuth] New token received: ${newToken}`,
				//   "color: green; font-weight: bold;"
				// );
				// Update token in the store
				dispatch(saveToken(newToken));
				// Delay the refetch to allow the store to update (adjust delay as needed)
				setTimeout(() => {
					refetch();
				}, 1000);
			} else if (
				data[objAccess].errorMessage === "IXIA_ACTIV_INVALID_ATTENDANCE" &&
				data[objAccess].errorCode === "err041"
			) {
				localStorage.setItem("checkedIn", false);
			} else if (
				data[objAccess].errorCode !== null &&
				data[objAccess].errorCode !== ""
			) {
				if (isVersion) logout();
				setTokenChange(false);
				enqueueSnackbar(t(data[objAccess].errorMessage), {
					variant: "error",
					autoHideDuration: 5000,
				});
			} else {
				setTokenChange(false);
				if (statusFunctions.onCompleted !== undefined)
					statusFunctions.onCompleted(data);
			}
		},
		onError: () => {
			if (isVersion) logout();
			setTokenChange(false);
			enqueueSnackbar(t("systemError"), {
				variant: "error",
				autoHideDuration: 5000,
			});
			if (statusFunctions.onError !== undefined)
				statusFunctions.onError(data);
		},
	});

	return {
		loading,
		error,
		data: refetchedData || data,
		refetch,
		tokenChange,
		reset: () => setTokenChange(true),
	};
}

export default useQueryAuth;
